// RunTable.tsx — a run history table, expandable one run at a time into the
// jobs it decomposed into. Used on the status route (every repo, most recent
// first) and on a repo's own page (that repo only), which is why the repo
// column is a prop rather than an assumption.
//
// The jobs are the reason a row expands at all. A run's headline numbers say
// how much work there was and how much of it landed; what they cannot say is
// the daemon's one hard ordering guarantee — every file summary finishes
// before any directory summary starts, and directories then run deepest-level
// first up to the repo root. Grouping the jobs by level shows that order
// directly, so a run stuck at level 0 is visibly stuck on files rather than
// merely "37/210".
//
// Jobs load on first expand and are kept across collapses: a bootstrap run of
// a large repository is thousands of jobs, far too many to ship with the run
// list, and reopening a row should cost nothing.
import { Fragment, useEffect, useState } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Expando, { ExpandoChevron } from 'common/ui/Expando';
import { StatusBadge } from 'common';
import { fetchRun } from '../api';
import type { Job, Run } from '../api';
import { EM_DASH, absoluteTime, elapsed, formatCount, formatUsd, relativeTime, shortSha } from '../format';
import { JobStatusBadge, RunStatusBadge } from './StatusBadges';

export default function RunTable({ runs, showRepo = false }: { runs: Run[]; showRepo?: boolean }) {
  if (runs.length === 0) {
    return <p className="text-muted text-center py-4 m-0">No runs yet.</p>;
  }
  return (
    <Table size="sm" hover responsive className="m-0">
      <thead>
        <tr>
          <th>Started</th>
          {showRepo && <th>Repository</th>}
          <th>Trigger</th>
          <th>Status</th>
          <th>Jobs</th>
          <th>Tokens</th>
          <th>Cost</th>
          <th>Commit</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((run) => (
          <RunRow key={run.id} run={run} showRepo={showRepo} />
        ))}
      </tbody>
    </Table>
  );
}

// RunRow is one run plus, when expanded, its jobs.
function RunRow({ run, showRepo }: { run: Run; showRepo: boolean }) {
  const columns = showRepo ? 8 : 7;
  return (
    <Expando>
      {({ open, toggle }) => (
        <Fragment>
          <tr>
            <td className="align-middle">
              <div className="d-flex align-items-center gap-2">
                {/* Row chrome matches the doc tree and imprinter's module
                    table: a link-variant chevron, never a filled control, so
                    a table of twenty rows carries twenty affordances and no
                    twenty buttons. */}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  onClick={toggle}
                  aria-expanded={open}
                  aria-label={open ? 'Collapse run' : 'Expand run'}
                  title={open ? 'Hide this run’s jobs' : 'Show this run’s jobs'}
                >
                  <ExpandoChevron open={open} />
                </Button>
                <span title={absoluteTime(run.started_at)}>{relativeTime(run.started_at)}</span>
              </div>
            </td>
            {showRepo && <td className="align-middle text-truncate">{run.repo_full_name}</td>}
            <td className="align-middle">
              {/* The trigger is a fact about provenance, not a state, so it
                  takes the tone that carries no state (DESIGN.md badges). */}
              <StatusBadge tone="neutral" uppercase={false}>{run.trigger}</StatusBadge>
            </td>
            <td className="align-middle">
              <RunStatusBadge status={run.status} />
              {run.error && <div className="text-danger small">{run.error}</div>}
            </td>
            <td className="align-middle" style={{ minWidth: '11rem' }}>
              <LevelProgress run={run} />
            </td>
            <td className="align-middle small text-muted" title="Prompt → completion tokens">
              {formatCount(run.prompt_tokens)} → {formatCount(run.completion_tokens)}
            </td>
            <td className="align-middle small">{formatUsd(run.cost_usd)}</td>
            <td className="align-middle small">
              {/* The documentation commit this run pushed as docdaemon itself
                  — empty until it commits, which is the difference between a
                  run that finished and one that changed the repository. */}
              <code>{shortSha(run.commit_sha)}</code>
              <div className="text-muted">{elapsed(run.started_at, run.finished_at)}</div>
            </td>
          </tr>
          <JobRows runID={run.id} open={open} columns={columns} />
        </Fragment>
      )}
    </Expando>
  );
}

// LevelProgress is the run's shape in one cell: how far through its jobs it
// is, then the decomposition underneath — files, directories, and how deep the
// deepest directory summary sits. The bar is the count; the caption is why the
// count is that size.
function LevelProgress({ run }: { run: Run }) {
  const done = run.total_jobs > 0 ? (run.done_jobs / run.total_jobs) * 100 : 0;
  const failed = run.total_jobs > 0 ? (run.failed_jobs / run.total_jobs) * 100 : 0;
  return (
    <div>
      <div className="d-flex justify-content-between small text-muted">
        <span>{run.done_jobs}/{run.total_jobs}</span>
        {run.failed_jobs > 0 && <span className="text-danger">{run.failed_jobs} failed</span>}
      </div>
      {/* Two stacked segments rather than one: a run that is 90% done with 10%
          of its jobs failed is not the same thing as one that is 90% done, and
          a single bar says they are. */}
      <ProgressBar className="mt-1" style={{ height: '0.4rem' }}>
        <ProgressBar now={done} variant="success" key="done" aria-label="jobs done" />
        <ProgressBar now={failed} variant="danger" key="failed" aria-label="jobs failed" />
      </ProgressBar>
      <div className="small text-muted mt-1">
        {run.file_jobs} file{run.file_jobs === 1 ? '' : 's'} · {run.dir_jobs} dir{run.dir_jobs === 1 ? '' : 's'}
        {run.max_level > 0 && <> · depth {run.max_level}</>}
      </div>
    </div>
  );
}

// JobRows fetches a run's jobs the first time it is opened and stays mounted
// while closed, rendering nothing — so collapsing and reopening costs no
// second request.
function JobRows({ runID, open, columns }: { runID: string; open: boolean; columns: number }) {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || jobs || error) return undefined;
    let live = true;
    void (async () => {
      try {
        const detail = await fetchRun(runID);
        if (live) setJobs(detail.jobs);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Failed to load jobs');
      }
    })();
    return () => { live = false; };
  }, [open, runID, jobs, error]);

  if (!open) return null;
  if (error) {
    return (
      <tr>
        <td colSpan={columns} className="text-danger small ps-5">Couldn’t load jobs: {error}</td>
      </tr>
    );
  }
  if (!jobs) {
    return (
      <tr>
        <td colSpan={columns} className="text-muted small ps-5">
          <Spinner animation="border" size="sm" /> Loading jobs…
        </td>
      </tr>
    );
  }
  if (jobs.length === 0) {
    return (
      <tr>
        <td colSpan={columns} className="text-muted small ps-5">
          No jobs — nothing this run touched needed re-documenting.
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td colSpan={columns} className="ps-5">
        <JobLevels jobs={jobs} />
      </td>
    </tr>
  );
}

// JobLevels groups jobs the way the scheduler runs them: level 0 is every
// file, then each directory level from the deepest up to the repo root. The
// heading per level is what makes the ordering legible — the levels are a
// sequence, not a category, and a flat list of two thousand paths is not.
function JobLevels({ jobs }: { jobs: Job[] }) {
  const levels = new Map<number, Job[]>();
  for (const job of jobs) {
    const bucket = levels.get(job.level);
    if (bucket) bucket.push(job);
    else levels.set(job.level, [job]);
  }
  const ordered = [...levels.keys()].sort((a, b) => a - b);
  return (
    <div className="d-flex flex-column gap-3 py-2">
      {ordered.map((level) => {
        const bucket = levels.get(level) ?? [];
        const done = bucket.filter((j) => j.status === 'done').length;
        return (
          <div key={level}>
            <div className="small text-muted mb-1">
              {level === 0 ? 'Level 0 · files' : `Level ${level} · directories`}
              {' — '}{done}/{bucket.length} done
            </div>
            <Table size="sm" borderless responsive className="m-0">
              <tbody>
                {bucket.map((job) => (
                  <tr key={job.id}>
                    <td className="align-middle"><code className="small">{job.path || '/'}</code></td>
                    <td className="align-middle small text-muted">{job.kind}</td>
                    <td className="align-middle"><JobStatusBadge status={job.status} /></td>
                    {/* Which model wrote it: a page produced by the overflow
                        model during a queue spike is not the same artifact as
                        one from the pinned local model. */}
                    <td className="align-middle small text-muted">{job.model || EM_DASH}</td>
                    <td className="align-middle small text-muted">
                      {formatCount(job.prompt_tokens)} → {formatCount(job.completion_tokens)}
                    </td>
                    <td className="align-middle small text-muted">
                      {Math.round(job.duration_ms / 1000)}s
                      {job.attempt > 1 && <> · attempt {job.attempt}</>}
                    </td>
                    <td className="align-middle small text-danger">{job.error}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        );
      })}
    </div>
  );
}
